import * as React from 'react';
import "./AcceptabilityDashboard.css";
import { connect, Dispatch } from 'react-redux';

import * as Actions from '../actions';
import { AnalyseState } from '../interfaces/state';
import { CoughAnalysisSetComment, CoughEventTypeEnum, OverreadStatusEnum, OverreadStatusIdEnum } from '../../../misc/api/apiTypes';
import { AuditModal } from "../../../auditmodal/components/AuditModal";
import once from 'lodash-es/once';
import { useWebPortalLegacyClient, useWebPortalApiClient } from '../../../misc/api/WebPortalTokenClient';
import { useHistory } from 'react-router-dom';
import { OnIgnoreEnter } from '../AnalyseApp';
import { CoughAnalysisSetTypeEnum } from '../../../misc/api/CoughAnalysisSetTypes';
import { CoughAnalysisSet, StudyCoughEventType } from 'webportalclient';
import { useQuery } from "../../../Components/Query";
import { SendForVisualReviewModalBody } from "../../../cough-analysis/SendForVisualReviewModal/SendForVisualReviewModal";
import { PeaksPoint } from '../interfacesPeaks/PeaksJS';

interface AcceptabilityDashboardProps {
    isVisible: boolean;
    primaryAnalysisSetId: string;
    acceptabilityModalVisiblility: boolean;
    selectedAcceptability: OverreadStatusEnum;
    isSubmitting: boolean;
    coughAnalysisSet: CoughAnalysisSet;
    requiresVisualReview: boolean;
    comments: CoughAnalysisSetComment[];
    dispatch?: Dispatch<AnalyseState>;
    errorStatus?: string | undefined;
    blindComments: boolean;
    pointEvents: PeaksPoint[];
    eventDuplicateThreshold: number;
    availableEventTypes: StudyCoughEventType[];
    rawTimepoints: Map<string, number>;
    selectedSegment: number;
}

export const AcceptabilityDashboard = (props: AcceptabilityDashboardProps) => {
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const [sendForVisualReviewVisible, setSendForVisualReviewVisible] = React.useState(false);
    const history = useHistory();
    const { refresh } = useQuery();
    
    React.useEffect(() => {
        props.dispatch(
            Actions.InitializeCurrentAnalysisSetAsyncAction.create(
                client,
                props.coughAnalysisSet.id
            ))
    }, []);

    const onSubmit = once((audit) => {
        props.dispatch(Actions.AcceptabilitySetAsyncAction.create(legacyClient, client.client, history, props.primaryAnalysisSetId, props.selectedAcceptability, audit.Annotation));
    });

    const onInitialSubmit = async (status: OverreadStatusEnum) => {
        props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(true));
        props.dispatch(Actions.SetAcceptanceErrorAction.create(null));

        for (const point of props.pointEvents) {
            point.withinDuplicateThreshold = false;
        }

        const coughPeakEvent = props.availableEventTypes.find(x => x.refEventTypeId === CoughEventTypeEnum.CoughPeak);

        const valuesInThreshold = props.pointEvents.filter(x => x.layerPosition === 0 && x.eventTypeId === coughPeakEvent.id)
            .filter((ev1, _, entries) =>
                entries.some((ev2) => ev1.id !== ev2.id && Math.abs((props.rawTimepoints.get(ev1.id) * 1000) - (props.rawTimepoints.get(ev2.id) * 1000)) <= props.eventDuplicateThreshold)
            );

        const remainingUnconfirmed = valuesInThreshold.filter(x => !x.confirmedValidDuplicate);

        if (remainingUnconfirmed.length > 0) {
            for (const point of remainingUnconfirmed) {
                point.withinDuplicateThreshold = true;
            }
            window.scrollTo(0, 0);
            const thresholdInSeconds = props.eventDuplicateThreshold / 1000;
            props.dispatch(Actions.SetAcceptanceErrorAction.create(`${remainingUnconfirmed.length} events are within the duplicate threshold of ${thresholdInSeconds}s please review before continuing`));
            props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(false));
            return;
        }

        props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(false));
        props.dispatch(Actions.ShowAcceptabilityAuditModalAction.create(status));
    }

    const handleSendAnalysisSetForVisualReview = (analysisSetId: string) => {
        setSendForVisualReviewVisible(true);

    }

    const isCorrectTypeForVisualReview = () => !props.coughAnalysisSet.isLinkedAnalysisSet && (props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.Analysis || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.QualityCheck || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.ProjectQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.MonitoringQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.DifficultCougherQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.WeeklyQualityAssurance);

    const canSendForVisualReview = (): boolean => {
        return isCorrectTypeForVisualReview() && (props.coughAnalysisSet.status === OverreadStatusIdEnum.Pending);
    }

    const isVisualReviewRequiredOrComplete = () => {
        if (!props.coughAnalysisSet) return false;

        const { requiresVisualReview, coughAnalysisSet } = props;

        if (coughAnalysisSet.visualReviewAnalystSelectionId === null && coughAnalysisSet.firstComplete !== null) {
            return false;
        }

        if (coughAnalysisSet.visualReviewAnalystSelectionId !== null) {

            switch (coughAnalysisSet.visualReviewAnalystSelectionId) {
                case 0:
                    return false;
                case 1:
                    return false;
                case 2:
                    return true;
                case undefined:
                    return true;
            }
        } else if (requiresVisualReview === true) {
            return true;
        } else {
            return false;
        }
       
    };

    if (props.isVisible) {
        return (
            <>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <div className="tooltip-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
                            <button className="btn btn-success" disabled={props.isSubmitting || isVisualReviewRequiredOrComplete()} onClick={() => { onInitialSubmit(OverreadStatusEnum.Accepted) }} onKeyDown={OnIgnoreEnter}>
                                <i className="fa fa-check" aria-hidden="true"></i>  Accept
                            </button>
                            {(props.isSubmitting || isVisualReviewRequiredOrComplete()) && (
                                <span className="tooltip-text">Complete Review before accepting</span>
                            )}
                        </div>&nbsp;
                        <button className="btn btn-warning" disabled={props.isSubmitting} onClick={() => props.dispatch(Actions.AcceptabilitySetAsyncAction.create(legacyClient, client.client, history, props.primaryAnalysisSetId, OverreadStatusEnum.Pending))} onKeyDown={OnIgnoreEnter}>
                            <i className="fa fa-circle-o" aria-hidden="true"></i> Pending
                        </button>&nbsp;
                        <button className="btn btn-danger" disabled={props.isSubmitting} onClick={() => { onInitialSubmit(OverreadStatusEnum.Rejected) }} onKeyDown={OnIgnoreEnter}>
                            <i className="fa fa-times" aria-hidden="true"></i> Reject
                        </button>
                        &nbsp;
                        {canSendForVisualReview() ?
                            <button className="btn btn-primary" disabled={props.isSubmitting} onClick={() => { handleSendAnalysisSetForVisualReview(props.primaryAnalysisSetId); }} onKeyDown={OnIgnoreEnter}>
                                {props.coughAnalysisSet.visualReviewAnalystSelectionId === 0 || props.coughAnalysisSet.visualReviewAnalystSelectionId === 1 ? "View/Add Review Comments" : "Send For Review" } 
                            </button> : null}
                    </div>
                </div>
                {props.acceptabilityModalVisiblility ?
                    <AuditModal header="Analysis Approval"
                        isSubmitting={props.isSubmitting}
                        onSubmit={onSubmit}
                        onClose={() => props.dispatch(Actions.CloseAcceptabilityAuditModalAction.create())} />
                    : null}
                {sendForVisualReviewVisible ?
                    <SendForVisualReviewModalBody
                        dispatch={props.dispatch}
                        comments={props.comments}
                        blindComments={props.blindComments}
                        coughAnalysisSet={props.coughAnalysisSet}
                        analysisSetId={props.coughAnalysisSet.id}
                        errorStatus={props.errorStatus}
                        selectedSegment={props.selectedSegment}
                        onClose={() => { setSendForVisualReviewVisible(false); refresh(); }} /> : null}
            </>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState): AcceptabilityDashboardProps {
    let pointEvents = state.core.peaksJs ? state.core.peaksJs.points.getPoints() as Array<PeaksPoint> : [] as Array<PeaksPoint>;

    return {
        isVisible: !state.core.isViewOnly,
        primaryAnalysisSetId: state.analysisSet.primaryAnalystSetId,
        acceptabilityModalVisiblility: state.core.isAcceptabilityAuditModalVisible,
        selectedAcceptability: state.core.acceptabilityStatus,
        isSubmitting: state.core.isSubmitting,
        coughAnalysisSet: state.core.coughAnalysisSet,
        requiresVisualReview: state.core.requiresVisualReview,
        comments: state.core.comments,
        blindComments: state.core.blindComments,
        pointEvents: pointEvents,
        eventDuplicateThreshold: state.core.eventDuplicateThreshold,
        availableEventTypes: state.core.availableEventTypes,
        rawTimepoints: state.core.rawTimestampPoints,
        selectedSegment: state.core.selectedSegment,
        errorStatus: state.core.visualInspectionErrorMessage,
    } as AcceptabilityDashboardProps;
}

export default connect<AcceptabilityDashboardProps, any, void>(mapStateToProps)(AcceptabilityDashboard);
